import React, {useMemo} from "react";
import {useMsal} from "@azure/msal-react";
import {FaListUl, FaTable} from "react-icons/fa";
import {MdAnalytics, MdBugReport, MdHome, MdMenuBook} from "react-icons/md";
import {TbCloudSearch} from "react-icons/tb";
import {ListChecks, Bot} from "lucide-react";

import Dashboard from "./views/admin/default";
import ChatIndex from "./views/details/chatIndex";
import SODDashboard from "views/admin/sod";
import CrawlerAnalytics from "views/admin/crawler";
import Dictionary from "views/admin/dictionary";
import Reference from "views/admin/reference";
import GreatAnalytics from "views/admin/great";
import ZohoTicketManager from "views/admin/zohoreview";
import NLPage from "./views/admin/nlp";
import {EmberAdmin, globalAdmins, NLPTEAM, retailrelationsteam, SUPERVISOR_TEAMS} from "./variables/constants";
import AdminTemplatesPage from "./views/admin/ember";


const isSupervisorOrTeamMember = (email) => {
  const normalizedEmail = email.toLowerCase();
  if (SUPERVISOR_TEAMS[normalizedEmail]) return true;
  for (const teamData of Object.values(SUPERVISOR_TEAMS)) {
    if (teamData.teamMembers.some((m) => m.email.toLowerCase() === normalizedEmail)) {
      return true;
    }
  }
  return false;
};

export const routes = [
  {
    name: "B2B Chat Dashboard",
    routeName: "/dashboard",
    layout: "",
    path: "",
    icon: <MdHome className="h-6 w-6" />,
    component: <Dashboard />,
  },
  {
    name: "B2B Chats History",
    routeName: "/all-chats",
    layout: "",
    path: "all-chats/:id?",
    icon: <FaListUl className="h-6 w-6" />,
    component: <ChatIndex />,
    secondary: true,
  },
  {
    name: "Custom Order Analytics",
    routeName: "/sod",
    layout: "",
    path: "sod",
    icon: <MdAnalytics className="h-6 w-6" />,
    component: <SODDashboard />,
    secondary: true,
  },
  {
    name: "Crawler Analytics",
    routeName: "/crawler",
    layout: "",
    path: "crawler",
    icon: <MdBugReport className="h-6 w-6" />,
    component: <CrawlerAnalytics />,
    secondary: true,
  },
  {
    name: "NLP Searches",
    layout: "",
    path: "nlp/:id?",
    icon: <TbCloudSearch className="h-6 w-6" />,
    component: <NLPage />,
    secondary: true,
  },
  {
    name: "Dictionary",
    routeName: "/dictionary",
    layout: "",
    path: "dictionary",
    icon: <MdMenuBook className="h-6 w-6" />,
    component: <Dictionary />,
    secondary: true,
  },
  {
    name: "Reference",
    routeName: "/reference",
    layout: "",
    path: "reference",
    icon: <FaTable className="h-6 w-6" />,
    component: <Reference />,
    secondary: true,
  },
  {
    // GREAT Analytics also supports an optional direction segment
    // (/great/outbound, or /outbound when this is the supervisor index route).
    // The extra ":direction" Route is mounted in src/layouts/main/index.jsx
    // (getRoutes) so it keeps working even after the first allowed route's
    // path is rewritten to "" for supervisors/team members.
    name: "GREAT Analytics",
    routeName: "/great",
    layout: "",
    path: "great",
    icon: <MdAnalytics className="h-6 w-6" />,
    component: <GreatAnalytics />,
    secondary: true,
  },
  {
    name: "Zoho Reviews",
    routeName: "/zoho-reviews",
    layout: "",
    path: "zoho-reviews",
    icon: <ListChecks className="h-6 w-6" />,
    component: <ZohoTicketManager />,
    secondary: true,
  },
  {
    name: "Ember Admin Panel",
    routeName: "/ember-admin",
    layout: "",
    path: "ember-admin",
    icon: <Bot className="h-6 w-6" />,
    component: <AdminTemplatesPage />,
    secondary: true,
  }
];

const RoutesWrapper = () => {
  const { accounts } = useMsal();

  const email = accounts[0]?.username?.toLowerCase();

  return useMemo(() => {
    if (!email) return [];

    let filteredRoutes = [];

    const normalizedEmail = email?.toLowerCase();

    if (globalAdmins.includes(normalizedEmail)) {
      filteredRoutes = routes.map((r) => ({ ...r }));

    } else if (isSupervisorOrTeamMember(normalizedEmail)) {
      filteredRoutes = routes
        .filter(route => route.name === "GREAT Analytics")
        .map((r) => ({ ...r }));

    } else if (retailrelationsteam.includes(normalizedEmail)) {
      filteredRoutes = routes
        .filter(route => route.name === "Zoho Reviews")
        .map((r) => ({ ...r }));
    }

    if (EmberAdmin.includes(normalizedEmail)) {
      const alreadyExists = filteredRoutes.some(
          r => r.name === "Ember Admin Panel"
      );

      if (!alreadyExists) {
        const emberRoute = routes.find(r => r.name === "Ember Admin Panel");
        if (emberRoute) {
          filteredRoutes.push({ ...emberRoute });
        }
      }
    }


    if (filteredRoutes.length > 0) {
      filteredRoutes[0].path = "";
    }

    // The GREAT Analytics nav link should open the in-page Summary view by
    // default (/great/summary, or /summary when GREAT is the rewritten index
    // route for supervisors/team members). The bare route (no segment) still
    // resolves to inbound — only the sidebar link target points at summary.
    filteredRoutes.forEach((r) => {
      if (r.name === "GREAT Analytics") {
        r.linkTo = r.path ? `/${r.path}/summary` : "/summary";
      }
    });

    return filteredRoutes;
  }, [email]);
};

export default RoutesWrapper;
